import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const NEON = "#E3FF00";

function LegalLayout({ title, updated, testid, children }) {
  return (
    <div className="min-h-screen bg-[#FDFDFC]" data-testid={testid}>
      <header className="border-b border-neutral-200 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="h-10 w-10 rounded-2xl grid place-items-center font-display font-extrabold text-lg text-neutral-900" style={{ backgroundColor: NEON }}>MP</span>
            <span className="font-display font-extrabold text-lg text-neutral-900">Mypasspartu.it</span>
          </Link>
          <Link to="/" data-testid="legal-back-home" className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-900 px-4 py-2.5 rounded-lg hover:brightness-95 transition-all" style={{ backgroundColor: NEON }}><ArrowLeft className="h-4 w-4" /> Home</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-neutral-900">{title}</h1>
        <p className="text-sm text-neutral-500 mt-2">Ultimo aggiornamento: {updated}</p>
        <div className="mt-8 space-y-7 text-neutral-700 leading-relaxed text-[15px]">{children}</div>
      </main>
    </div>
  );
}

const H = ({ children }) => <h2 className="font-display font-bold text-xl text-neutral-900 mb-2">{children}</h2>;
const Sec = ({ title, children }) => <section><H>{title}</H>{children}</section>;

export function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" updated="Giugno 2026" testid="privacy-policy-page">
      <p>La presente informativa descrive come Mypasspartu.it tratta i dati personali degli utenti, in conformità al Regolamento (UE) 2016/679 (GDPR) e al D.Lgs. 196/2003 e s.m.i.</p>
      <Sec title="1. Titolare del trattamento">
        <p>Il Titolare del trattamento è Mypasspartu.it. Per qualsiasi richiesta relativa ai tuoi dati puoi scrivere a <a href="mailto:info@mypasspartu.it" className="text-neutral-900 underline font-medium">info@mypasspartu.it</a>.</p>
      </Sec>
      <Sec title="2. Dati che raccogliamo">
        <p>Raccogliamo: dati di registrazione (nome, email, nome azienda, password cifrata); dati di utilizzo della piattaforma e dei servizi dell'ecosistema; dati forniti volontariamente tramite i moduli di contatto e di candidatura servizio (azienda, referente, email, telefono, sito web, descrizione del servizio); dati tecnici e di navigazione tramite cookie.</p>
      </Sec>
      <Sec title="3. Finalità e basi giuridiche">
        <p>Trattiamo i dati per: erogare i servizi richiesti e gestire l'account (esecuzione del contratto); inviare comunicazioni di servizio e, previo consenso, comunicazioni informative; adempiere a obblighi di legge, contabili e fiscali; prevenire abusi e garantire la sicurezza (legittimo interesse).</p>
      </Sec>
      <Sec title="4. Conservazione dei dati">
        <p>Conserviamo i dati per il tempo necessario alle finalità indicate e, successivamente, per i termini previsti dalla legge. I dati dell'account sono conservati fino alla cancellazione dell'account e nei limiti degli obblighi di legge.</p>
      </Sec>
      <Sec title="5. Comunicazione a terzi">
        <p>I dati possono essere trattati da fornitori che erogano servizi per nostro conto (responsabili del trattamento), tra cui: il provider email per l'invio delle comunicazioni, i fornitori dei servizi integrati nell'ecosistema e i provider di pagamento. Non vendiamo i tuoi dati a terzi.</p>
      </Sec>
      <Sec title="6. Diritti dell'interessato">
        <p>Hai diritto di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione al trattamento, oltre al diritto di revocare il consenso in qualsiasi momento. Puoi esercitare i tuoi diritti scrivendo a <a href="mailto:info@mypasspartu.it" className="text-neutral-900 underline font-medium">info@mypasspartu.it</a>. Hai inoltre diritto di proporre reclamo al Garante per la protezione dei dati personali.</p>
      </Sec>
      <Sec title="7. Cookie">
        <p>L'utilizzo dei cookie è descritto nella <Link to="/cookie-policy" className="text-neutral-900 underline font-medium">Cookie Policy</Link>.</p>
      </Sec>
    </LegalLayout>
  );
}

export function CookiePolicy() {
  return (
    <LegalLayout title="Cookie Policy" updated="Giugno 2026" testid="cookie-policy-page">
      <p>Questa pagina spiega come e perché Mypasspartu.it utilizza i cookie e tecnologie simili.</p>
      <Sec title="1. Cosa sono i cookie">
        <p>I cookie sono piccoli file di testo che i siti salvano sul dispositivo dell'utente per memorizzare informazioni e preferenze.</p>
      </Sec>
      <Sec title="2. Tipologie utilizzate">
        <p><strong>Cookie tecnici necessari</strong>: indispensabili per il funzionamento della piattaforma e per l'autenticazione. Non richiedono consenso.</p>
        <p><strong>Cookie funzionali</strong>: memorizzano le tue preferenze per migliorare l'esperienza.</p>
        <p><strong>Cookie analitici</strong>: ci aiutano a capire come viene utilizzata la piattaforma in forma aggregata. Vengono installati solo con il tuo consenso.</p>
      </Sec>
      <Sec title="3. Gestione delle preferenze">
        <p>Al primo accesso puoi scegliere se accettare tutti i cookie o solo quelli essenziali. Puoi modificare in ogni momento le impostazioni dei cookie dal tuo browser ed eliminare quelli già salvati.</p>
      </Sec>
      <Sec title="4. Contatti">
        <p>Per informazioni sui cookie scrivi a <a href="mailto:info@mypasspartu.it" className="text-neutral-900 underline font-medium">info@mypasspartu.it</a>.</p>
      </Sec>
    </LegalLayout>
  );
}

export function Terms() {
  return (
    <LegalLayout title="Termini di Servizio" updated="Giugno 2026" testid="terms-page">
      <Sec title="1. Oggetto">
        <p>I presenti Termini regolano l'accesso e l'uso della piattaforma Mypasspartu.it e dei servizi integrati nel suo ecosistema.</p>
      </Sec>
      <Sec title="2. Account e responsabilità">
        <p>L'utente è responsabile della veridicità dei dati forniti e della custodia delle proprie credenziali. È vietato ogni uso illecito o non autorizzato della piattaforma.</p>
      </Sec>
      <Sec title="3. Borsellino prepagato e servizi di terzi">
        <p>Alcuni servizi possono prevedere l'uso di un borsellino prepagato. I servizi di terze parti integrati sono erogati dai rispettivi fornitori secondo i loro termini e condizioni.</p>
      </Sec>
      <Sec title="4. Limitazione di responsabilità">
        <p>La piattaforma è fornita "così com'è". Nei limiti di legge, non rispondiamo di danni indiretti derivanti dall'uso dei servizi di terze parti.</p>
      </Sec>
      <Sec title="5. Legge applicabile">
        <p>I presenti Termini sono regolati dalla legge italiana. Per ogni controversia è competente il foro del luogo di residenza o domicilio del consumatore, ove applicabile.</p>
      </Sec>
      <Sec title="6. Contatti">
        <p>Per qualsiasi informazione: <a href="mailto:info@mypasspartu.it" className="text-neutral-900 underline font-medium">info@mypasspartu.it</a>.</p>
      </Sec>
    </LegalLayout>
  );
}
